import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { DocumentRowUi } from "../../types/documentManagementUiTypes";

export type DeleteConfirmState =
  | {
      source: "preview";
      idx: number;
      documentId: number;
      fileName: string;
    }
  | { source: "list"; documentId: number; fileName: string }
  | {
      source: "bulk";
      items: { documentId: number; fileName: string }[];
    };

export type DocumentManagementDialogsProps = {
  renameOpen: boolean;
  renameBusy: boolean;
  renameName: string;
  renameDescription: string;
  onRenameNameChange: (value: string) => void;
  onRenameDescriptionChange: (value: string) => void;
  onRenameClose: () => void;
  onRenameSubmit: () => void;
  disableConfirm: DocumentRowUi | null;
  lifecycleBusyId: number | null;
  onDisableClose: () => void;
  onDisableConfirm: () => void;
  deleteConfirm: DeleteConfirmState | null;
  deletingDocumentId: number | null;
  bulkDeleteBusy: boolean;
  dontShowDeleteConfirmAgain: boolean;
  onDontShowDeleteConfirmAgainChange: (checked: boolean) => void;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
};

export function DocumentManagementDialogs({
  renameOpen,
  renameBusy,
  renameName,
  renameDescription,
  onRenameNameChange,
  onRenameDescriptionChange,
  onRenameClose,
  onRenameSubmit,
  disableConfirm,
  lifecycleBusyId,
  onDisableClose,
  onDisableConfirm,
  deleteConfirm,
  deletingDocumentId,
  bulkDeleteBusy,
  dontShowDeleteConfirmAgain,
  onDontShowDeleteConfirmAgainChange,
  onDeleteClose,
  onDeleteConfirm,
}: DocumentManagementDialogsProps) {
  const { t } = useTranslation();

  return (
    <>
      <Dialog
        open={renameOpen}
        onClose={() => (!renameBusy ? onRenameClose() : undefined)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("opva.dmRenameDatasetTitle", {
            defaultValue: "Rename dataset",
          })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label={t("opva.dmNewDatasetName", {
                defaultValue: "Dataset name",
              })}
              value={renameName}
              onChange={(e) => onRenameNameChange(e.target.value)}
              fullWidth
              required
              disabled={renameBusy}
            />
            <TextField
              label={t("opva.dmNewDatasetDescription", {
                defaultValue: "Description (optional)",
              })}
              value={renameDescription}
              onChange={(e) => onRenameDescriptionChange(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              disabled={renameBusy}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onRenameClose} disabled={renameBusy}>
            {t("opva.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            disabled={renameBusy || !renameName.trim()}
            onClick={onRenameSubmit}
            startIcon={
              renameBusy ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {renameBusy
              ? t("opva.dmSaving", { defaultValue: "Saving…" })
              : t("opva.save", { defaultValue: "Save" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={disableConfirm !== null}
        onClose={onDisableClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t("opva.dmDisableTitle", { defaultValue: "Disable document?" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("opva.dmDisableWarning", {
              defaultValue:
                '"{{name}}" will be hidden from retrieval. You can enable it again later.',
              name: disableConfirm?.original_file_name ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onDisableClose} disabled={lifecycleBusyId !== null}>
            {t("opva.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={lifecycleBusyId !== null || !disableConfirm}
            onClick={onDisableConfirm}
          >
            {t("opva.dmActionDisable", { defaultValue: "Disable" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirm !== null}
        onClose={onDeleteClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {deleteConfirm?.source === "bulk"
            ? t("opva.dmBulkDeleteTitle", {
                defaultValue: "Delete {{count}} documents?",
                count: deleteConfirm.items.length,
              })
            : t("opva.dmDeleteTitle", { defaultValue: "Delete document?" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            {deleteConfirm?.source === "bulk"
              ? t("opva.dmBulkDeleteWarning", {
                  defaultValue:
                    "This will permanently remove the selected documents from the vector database (Qdrant), file storage, and all database records. This cannot be undone.",
                })
              : deleteConfirm?.source === "list"
                ? t("opva.dmDeleteWarningFull", {
                    defaultValue:
                      "This will permanently remove the document from the vector database (Qdrant), file storage, and all database records. This cannot be undone.",
                  })
                : t("opva.dmDeleteWarning", {
                    defaultValue:
                      "This will permanently delete this document and its stored data. You'll need to upload it again to use it.",
                  })}
          </DialogContentText>
          {deleteConfirm?.source === "preview" ? (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={dontShowDeleteConfirmAgain}
                  onChange={(_, checked) =>
                    onDontShowDeleteConfirmAgainChange(checked)
                  }
                  disabled={deletingDocumentId !== null}
                />
              }
              label={t("opva.dmDeleteDontAskAgain", {
                defaultValue: "Don't show this warning again",
              })}
            />
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onDeleteClose}
            disabled={deletingDocumentId !== null || bulkDeleteBusy}
          >
            {t("opva.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingDocumentId !== null || bulkDeleteBusy}
            onClick={onDeleteConfirm}
            startIcon={
              deletingDocumentId !== null || bulkDeleteBusy ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {deletingDocumentId !== null || bulkDeleteBusy
              ? t("opva.dmDeleting", { defaultValue: "Deleting…" })
              : t("opva.delete", { defaultValue: "Delete" })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
